import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowRight, Scale, Handshake } from "lucide-react";
import Reveal from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ELIGIBILITY = [
  "Adults and young people seeking to build practical, social or emotional skills",
  "Voluntary, involuntary and mandated participants — all welcome",
  "Participants on bail conditions or other justice-system orders",
  "Programs flexible to participant capacity, schedule, and goals",
  "NDIS, justice-funded, private and self-funded pathways accepted",
];

const STEPS = [
  { n: "01", title: "Submit referral", body: "Use the form below or call our intake line. We acknowledge within one business day." },
  { n: "02", title: "Initial conversation", body: "We meet with you and (where possible) the participant to understand needs and goals." },
  { n: "03", title: "Match the programs", body: "We recommend the right program (or combination) and confirm fit with the participant." },
  { n: "04", title: "Begin & stay in touch", body: "Programs commence. You receive regular updates and remain part of the plan." },
];

const initial = {
  referrer_name: "", organisation: "", role: "", email: "", phone: "",
  client_initials: "", client_age: "", primary_concern: "", funding_source: "",
  urgency: "standard", notes: "",
};

export default function Professionals() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.referrer_name || !form.organisation || !form.role || !form.email || !form.phone) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/referrals`, form);
      setDone(true);
      toast.success("Referral received. We'll be in touch within one business day.");
      setForm(initial);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-professionals">
      <section className="bg-warm-50 pt-24 pb-20 lg:pt-32 lg:pb-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-12">
          <p className="text-xs uppercase tracking-[0.25em] text-brand font-semibold mb-6">For professionals</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-ink-900 max-w-4xl">
            Refer a participant. <em className="text-brand not-italic">We'll meet them where they are.</em>
          </h1>
          <p className="mt-7 text-lg lg:text-xl leading-relaxed text-ink-500 max-w-3xl">
            We're built for case managers and the realities of community work — minimal forms,
            fast acknowledgement, and a qualified counsellor (not a queue) on the other end.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <h2 className="font-serif text-3xl lg:text-4xl tracking-tight text-ink-900 mb-6">Eligibility at a glance</h2>
            <ul className="space-y-3">
              {ELIGIBILITY.map((e) => (
                <li key={e} className="flex items-start gap-3 text-ink-700">
                  <CheckCircle2 className="w-5 h-5 text-brand shrink-0 mt-0.5" />
                  <span>{e}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <div className="lg:col-span-7">
            <h2 className="font-serif text-3xl lg:text-4xl tracking-tight text-ink-900 mb-8">Referral process</h2>
            <ol className="space-y-px bg-warm-200/40 rounded-2xl overflow-hidden border border-warm-200/60">
              {STEPS.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.05}>
                  <li data-testid={`step-${s.n}`} className="bg-warm-50 p-7 lg:p-8 flex items-start gap-6">
                    <span className="font-serif text-3xl text-brand">{s.n}</span>
                    <div>
                      <h3 className="font-serif text-xl text-ink-900 mb-1">{s.title}</h3>
                      <p className="text-ink-500 leading-relaxed">{s.body}</p>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* JUSTICE & INVOLUNTARY ENGAGEMENT */}
      <section className="py-20 lg:py-28 bg-ink-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.25em] text-brand-soft font-semibold mb-5">Our specialty</p>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight">
              We engage the people other services can't.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-white/70">
              Some participants arrive mandated. Some have been through three services already.
              Some have decided support isn't for them. We're built for those participants.
            </p>
          </Reveal>
          <div className="lg:col-span-7 space-y-6">
            <Reveal>
              <div data-testid="involuntary-block" className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 lg:p-10 hover:bg-white/[0.06] transition-colors">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-soft/15 text-brand-soft mb-5">
                  <Handshake className="w-6 h-6" />
                </div>
                <h3 className="font-serif text-2xl mb-2">Involuntary & resistant participants</h3>
                <p className="text-white/70 leading-relaxed">
                  Our facilitators are skilled at building rapport with people who don't want to be in
                  the room. We use motivational, relational and skill-focused approaches that move
                  participants from compliance to genuine engagement.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div data-testid="justice-block" className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 lg:p-10 hover:bg-white/[0.06] transition-colors">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-soft/15 text-brand-soft mb-5">
                  <Scale className="w-6 h-6" />
                </div>
                <h3 className="font-serif text-2xl mb-2">Justice system & magistrate referrals</h3>
                <p className="text-white/70 leading-relaxed">
                  We work directly with magistrates, legal practitioners and corrections case managers
                  to provide structured programs that can support bail conditions and community-based
                  orders. We provide clear documentation of attendance, participation and progress.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* REFERRAL FORM */}
      <section id="form" className="py-20 lg:py-28 bg-warm-50">
        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-brand font-semibold mb-5">Referral form</p>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight text-ink-900">Begin a referral</h2>
            <p className="mt-4 text-ink-500 text-lg">Takes ~2 minutes. We'll respond within one business day.</p>
          </Reveal>

          {done ? (
            <div data-testid="referral-success" className="mt-12 rounded-2xl bg-white border border-warm-200/60 p-10 text-center">
              <CheckCircle2 className="w-12 h-12 text-brand mx-auto mb-4" />
              <h3 className="font-serif text-3xl text-ink-900 mb-2">Referral received</h3>
              <p className="text-ink-500 max-w-md mx-auto">
                Thank you. A member of our intake team will be in touch within one business day.
              </p>
              <button
                data-testid="submit-another"
                onClick={() => setDone(false)}
                className="mt-6 inline-flex items-center gap-2 text-brand font-semibold hover:gap-3 transition-all"
              >
                Submit another <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="referral-form" className="mt-12 bg-white border border-warm-200/60 rounded-2xl p-8 lg:p-12 space-y-8">
              <div>
                <p className="text-xs uppercase tracking-widest text-ink-400 mb-5">About you</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Your name *" id="referrer_name">
                    <Input id="referrer_name" data-testid="input-referrer-name" value={form.referrer_name} onChange={set("referrer_name")} required />
                  </Field>
                  <Field label="Organisation *" id="organisation">
                    <Input id="organisation" data-testid="input-organisation" value={form.organisation} onChange={set("organisation")} required />
                  </Field>
                  <Field label="Your role *" id="role">
                    <Input id="role" data-testid="input-role" value={form.role} onChange={set("role")} placeholder="Case manager, GP, social worker…" required />
                  </Field>
                  <Field label="Email *" id="email">
                    <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={set("email")} required />
                  </Field>
                  <Field label="Phone *" id="phone">
                    <Input id="phone" data-testid="input-phone" value={form.phone} onChange={set("phone")} required />
                  </Field>
                  <Field label="Urgency" id="urgency">
                    <Select value={form.urgency} onValueChange={(v) => setForm((f) => ({ ...f, urgency: v }))}>
                      <SelectTrigger data-testid="select-urgency" id="urgency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="priority">Priority (within 5 days)</SelectItem>
                        <SelectItem value="urgent">Urgent (within 48 hours)</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-widest text-ink-400 mb-5">About the participant</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Participant initials" id="client_initials">
                    <Input id="client_initials" data-testid="input-client-initials" value={form.client_initials} onChange={set("client_initials")} placeholder="e.g. JD" />
                  </Field>
                  <Field label="Participant age" id="client_age">
                    <Input id="client_age" data-testid="input-client-age" value={form.client_age} onChange={set("client_age")} />
                  </Field>
                  <Field label="Primary goals / needs" id="primary_concern">
                    <Input id="primary_concern" data-testid="input-primary-concern" value={form.primary_concern} onChange={set("primary_concern")} placeholder="Skill building, engagement, social participation…" />
                  </Field>
                  <Field label="Funding pathway" id="funding_source">
                    <Select value={form.funding_source} onValueChange={(v) => setForm((f) => ({ ...f, funding_source: v }))}>
                      <SelectTrigger data-testid="select-funding" id="funding_source"><SelectValue placeholder="Select funding…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ndis">NDIS</SelectItem>
                        <SelectItem value="justice">Justice / corrections funded</SelectItem>
                        <SelectItem value="private_health">Private health insurance</SelectItem>
                        <SelectItem value="self_funded">Self funded</SelectItem>
                        <SelectItem value="eap">EAP</SelectItem>
                        <SelectItem value="other">Other / unsure</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="Background & context" id="notes" className="mt-5">
                  <Textarea
                    id="notes"
                    data-testid="textarea-notes"
                    rows={5}
                    value={form.notes}
                    onChange={set("notes")}
                    placeholder="Brief background, current supports, justice involvement, previous engagement history, anything we should know."
                  />
                </Field>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                <p className="text-xs text-ink-400 max-w-md">
                  By submitting, you confirm you have informed consent to share this information with Neurogility.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-referral"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white px-7 py-4 text-sm font-semibold hover:bg-brand-hover disabled:opacity-60 transition-colors"
                >
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Submit referral <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, id, children, className = "" }) {
  return (
    <div className={className}>
      <Label htmlFor={id} className="text-sm text-ink-700 mb-2 block font-medium">{label}</Label>
      {children}
    </div>
  );
}
